import axios from 'axios';

// Detectar automáticamente la URL base según donde se ejecute el frontend
const getApiBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;

    // Si es localhost, usar puerto directo para desarrollo
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `http://localhost:4000/api`;
    }

    // Si es dominio de producción, usar el proxy de nginx (mismo dominio)
    return `${window.location.protocol}//${window.location.host}/api`;
  }

  // Fallback para server-side rendering
  return 'http://localhost:4000/api';
};

const API_BASE_URL = getApiBaseUrl();

// Configurar axios con interceptores
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para añadir token a todas las requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores de autenticación
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Si es un force logout específico, limpiar y redirigir
      if (error.response?.data?.forceLogout) {
        localStorage.removeItem('token');
        localStorage.removeItem('currentUser');
        alert(error.response.data.message || 'Tu sesión fue cerrada por un administrador');
        window.location.href = '/login';
        return Promise.reject(error);
      }
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API de autenticación
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  
  me: () =>
    api.get('/auth/me'),
  
  logout: () =>
    api.post('/auth/logout'),


  register: (userData: any) =>
    api.post('/auth/register', userData),
};

// API de usuarios
export const usersApi = {
  getAll: (params?: any) =>
    api.get('/users', { params }),
  
  getById: (id: number) =>
    api.get(`/users/${id}`),
  
  create: (userData: any) =>
    api.post('/users', userData),
  
  update: (id: number, userData: any) =>
    api.put(`/users/${id}`, userData),
  
  delete: (id: number) =>
    api.delete(`/users/${id}`),

  updatePermissions: (id: number, permissions: any) =>
    api.patch(`/users/${id}/permissions`, { permissions }),

  activate: (id: number, isActive: boolean) =>
    api.post(`/users/${id}/activate`, { isActive }),

  getLoginHistory: (id: number, limit?: number) =>
    api.get(`/users/${id}/login-history`, { params: { limit } }),
};

// API de clientes
export const clientsApi = {
  getAll: (params?: any) =>
    api.get('/clients', { params }),
  
  getById: (id: number) =>
    api.get(`/clients/${id}`),
  
  create: (clientData: any) =>
    api.post('/clients', clientData),
  
  update: (id: number, clientData: any) =>
    api.put(`/clients/${id}`, clientData),
  
  delete: (id: number) =>
    api.delete(`/clients/${id}`),

  getAudits: (id: number, params?: any) =>
    api.get(`/clients/${id}/audits`, { params }),

  getAccountStatement: (id: number) =>
    api.get(`/clients/${id}/account-statement`),

  // Obtener clientes disponibles para auditorías según rol
  getForAudits: () =>
    api.get('/clients/for-audits?onlyActive=true'),

  // Obtener auditorías del cliente actual (solo para clientes)
  getMyAudits: () =>
    api.get('/clients/my-audits'),

  // Gestión de sucursales
  getBranches: (clientId: number) =>
    api.get(`/clients/${clientId}/branches`),

  createBranch: (clientId: number, branchData: any) =>
    api.post(`/clients/${clientId}/branches`, branchData),

  updateBranch: (clientId: number, branchId: number, branchData: any) =>
    api.put(`/clients/${clientId}/branches/${branchId}`, branchData),
  deleteBranch: (clientId: number, branchId: number) =>
    api.delete(`/clients/${clientId}/branches/${branchId}`),
};

// API de auditorías
export const auditsApi = {
  getAll: (params?: any) =>
    api.get('/audits', { params }),
  
  getById: (id: number) =>
    api.get(`/audits/${id}`),
  
  create: (auditData: any) =>
    api.post('/audits', auditData),
  
  assign: (id: number, operadorId: number) =>
    api.patch(`/audits/${id}/assign`, { operadorId }),
  
  take: (id: number) =>
    api.patch(`/audits/${id}/take`),

  takeAssigned: (id: number) =>
    api.patch(`/audits/${id}/take-assigned`),

  start: (id: number) =>
    api.patch(`/audits/${id}/start`),
  
  complete: (id: number, data: any) =>
    api.patch(`/audits/${id}/complete`, data),

  autosave: (id: number, data: any) =>
    api.patch(`/audits/${id}/autosave`, data),
  
  approve: (id: number, aprobada: boolean, comentarios?: string) =>
    api.put(`/audits/${id}/send-to-client`, { 
      observacionesSupervisor: comentarios 
    }),

  getTemplate: (id: number) =>
    api.get(`/audits/${id}/template`),

  downloadFile: (auditId: number, filename: string) => {
    const token = localStorage.getItem('token');
    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/audits/${auditId}/download-attachment/${encodeURIComponent(filename)}`;
    
    // Crear un enlace temporal para descargar
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.download = filename;
    
    // Agregar token al header usando fetch
    return fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }).then(response => {
      if (!response.ok) {
        return response.text().then(text => {
          console.error('Error response:', text);
          throw new Error(`Error ${response.status}: ${text || 'Error descargando archivo'}`);
        });
      }
      return response.blob();
    }).then(blob => {
      const downloadUrl = window.URL.createObjectURL(blob);
      link.href = downloadUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    });
  },

  // Gestión de archivos (solo admin)
  getAllFiles: () =>
    api.get('/audits/files'),

  deleteFile: (auditId: number, filename: string) =>
    api.delete(`/audits/${auditId}/files/${filename}`),

  // Toggle estado gratuito (solo admin)
  toggleGratis: (auditId: number, gratisParaCliente: boolean, motivo?: string) =>
    api.put(`/audits/${auditId}/toggle-gratis`, {
      gratisParaCliente,
      motivo
    }),

  // Gestión de auditorías (solo admin)
  getAllForManagement: () =>
    api.get('/audits/management'),

  deleteAudit: (auditId: number, type: 'cleanup' | 'correction') =>
    api.delete(`/audits/${auditId}`, { data: { type } }),

  returnForCorrection: (auditId: number, reason: string) => {
    return api.put(`/audits/${auditId}/return-for-correction`, { reason });
  },

  devolverOperador: (auditId: number, reason: string) => {
    return api.post(`/audits/${auditId}/devolver-operador`, { reason });
  },

  reassignAudit: (auditId: number, operadorId?: number, mensaje?: string, prioridad?: string) =>
    api.put(`/audits/${auditId}/reassign`, { operadorId, mensaje, prioridad }),

  addMessage: (auditId: number, mensaje: string, tipo: 'operador' | 'supervisor' | 'general' = 'general') =>
    api.put(`/audits/${auditId}/add-message`, { mensaje, tipo }),

  generateWordReport: (auditId: number) => {
    const token = localStorage.getItem('token');
    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/audits/${auditId}/report/word`;
    
    return fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }).then(response => {
      if (!response.ok) {
        throw new Error('Error generando reporte Word');
      }
      return response.blob();
    });
  },

  // Obtener auditorías pendientes de revisión (supervisor)
  getPendingReview: () =>
    api.get('/audits/supervisor/pending'),

  // Obtener todas las auditorías donde el supervisor ha participado
  getMyAudits: () =>
    api.get('/audits/supervisor/my-audits'),

  // Obtener historial completo del operador
  getOperatorHistory: () =>
    api.get('/audits/operador/historial'),

  // Obtener historial completo del supervisor
  getSupervisorHistory: () =>
    api.get('/audits/supervisor/my-audits'),

  // Obtener datos completos para formulario editable del supervisor
  getSupervisorFormData: (auditId: number) =>
    api.get(`/audits/${auditId}/supervisor/form-data`),

  // Aprobar auditoría con cambios del supervisor
  supervisorApprove: (auditId: number, data: {
    observacionesSupervisor?: string;
    observacionesFinales?: string;
    eventosEditados?: any[];
    wordApproved?: boolean;
    titulo?: string;
  }) =>
    api.put(`/audits/supervisor/${auditId}/generate-pdf-for-client`, data),

  // Generar PDF final para cliente
  generatePdfForClient: (auditId: number, titulo?: string) =>
    api.get(`/audits/${auditId}/generate/pdf-client`, { params: { titulo } }),

  // Reanudar auditoría pausada
  resumeAudit: (auditId: number) =>
    api.patch(`/audits/${auditId}/resume`),

  // Descargar archivo Word generado
  downloadWord: (auditId: number) => {
    const token = localStorage.getItem('token');
    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/audits/${auditId}/download-word`;
    
    return fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }).then(response => {
      if (!response.ok) {
        throw new Error('Error descargando archivo Word');
      }
      return response.blob();
    });
  },

  // Obtener reporte HTML para visualización
  getHTMLReport: (auditId: number, printMode: boolean = false) => {
    const token = localStorage.getItem('token');
    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/audits/${auditId}/report/html${printMode ? '?print=true' : ''}`;
    
    return fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }).then(response => {
      if (!response.ok) {
        throw new Error('Error obteniendo reporte HTML');
      }
      return response.text();
    });
  },

  // Descargar PDF de la auditoría
  downloadPDF: (auditId: number) => {
    const token = localStorage.getItem('token');
    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/audits/${auditId}/report/pdf`;

    return fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }).then(response => {
      if (!response.ok) {
        return response.text().then(text => {
          console.error('Error response:', text);
          throw new Error(`Error ${response.status}: ${text || 'Error descargando PDF'}`);
        });
      }
      // Extraer el nombre del archivo del header Content-Disposition
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `auditoria-${auditId}.pdf`; // fallback
      if (contentDisposition) {
        // Primero intentar con formato UTF-8: filename*=UTF-8''encoded-filename
        const utf8Match = /filename\*=UTF-8''(.+)/.exec(contentDisposition);
        if (utf8Match && utf8Match[1]) {
          filename = decodeURIComponent(utf8Match[1]);
        } else {
          // Fallback a formato normal: filename="filename"
          const normalMatch = /filename="?([^"]+)"?/.exec(contentDisposition);
          if (normalMatch && normalMatch[1]) {
            filename = normalMatch[1];
          }
        }
      }
      return response.blob().then(blob => ({ blob, filename }));
    }).then(({ blob, filename }) => {
      // Crear enlace de descarga automática
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    });
  },

  // Alertas de tiempo
  getTimeAlerts: (params?: any) =>
    api.get('/time-alerts', { params }),

  markTimeAlertAsViewed: (alertId: number) =>
    api.put(`/time-alerts/${alertId}/viewed`),

  resolveTimeAlert: (alertId: number, resolution?: string) =>
    api.put(`/time-alerts/${alertId}/resolve`, { resolution })
};

// API del dashboard
export const dashboardApi = {
  getStats: () =>
    api.get('/dashboard/stats'),
  
  getAlerts: () =>
    api.get('/dashboard/alerts'),
};

// API de bloques (configuración)
export const blocksApi = {
  getAll: () =>
    api.get('/blocks'),
  
  getById: (id: number) =>
    api.get(`/blocks/${id}`),
  
  create: (blockData: any) =>
    api.post('/blocks', blockData),
};

// API de configuración
export const configApi = {
  getAuditPricing: () =>
    api.get('/config/audit-pricing'),
  
  updateAuditPricing: (config: any) =>
    api.put('/config/audit-pricing', config),
  
  calculateAuditCost: (params: any) =>
    api.post('/config/calculate-audit-cost', params),

  // Información de empresa
  getCompanyInfo: () =>
    api.get('/config/company-info'),

  updateCompanyInfo: (data: {
    nombre: string;
    email: string;
    telefono: string;
    direccion: string;
  }) =>
    api.put('/config/company-info', data),
};

// API de finanzas
export const finanzasApi = {
  getDashboard: () =>
    api.get('/finanzas/dashboard'),
  
  getMovimientos: () =>
    api.get('/finanzas/movimientos'),
    
  procesarCobro: (data: { clienteId: number; monto: number; concepto?: string }) =>
    api.post('/finanzas/procesar-cobro', data),
    
  pagarSueldo: (data: { empleadoId: number; monto: number; concepto?: string; periodo?: string }) =>
    api.post('/finanzas/pagar-sueldo', data),
  
  revertirPago: (data: { movimientoId: number | string; motivo?: string }) => {
    return api.post('/finanzas/revertir-pago', data);
  },
  
  // Mantener compatibilidad con endpoints existentes
  getClientes: () =>
    api.get('/finanzas/clientes'),
  
  getOperadores: () =>
    api.get('/finanzas/operadores'),
  
  getSueldos: () =>
    api.get('/finanzas/sueldos'),
  
  getDeudasClientes: () =>
    api.get('/finanzas/deudas-clientes'),
  
  getUsuariosDetallados: () =>
    api.get('/finanzas/usuarios/detallados'),
  
  create: (data: any) =>
    api.post('/finanzas', data),
  
  getAll: () =>
    api.get('/finanzas'),
};

// API de audit finanzas
export const auditFinanceApi = {
  processPayment: (data: any) =>
    api.post('/audit-finance/process-payment', data),
  
  processForgiveness: (data: any) =>
    api.post('/audit-finance/process-forgiveness', data),
  
  changePriority: (data: any) =>
    api.post('/audit-finance/change-priority', data),
  
  processClientPayment: (data: any) =>
    api.post('/audit-finance/process-client-payment', data),
  
  forgiveClient: (data: any) =>
    api.post('/audit-finance/forgive-client', data),
  
  claimDebt: (data: any) =>
    api.post('/audit-finance/claim-debt', data),
  
  debugMovimientos: () =>
    api.get('/audit-finance/debug-movimientos'),
  
  revertPayment: (data: any) =>
    api.post('/audit-finance/revert-payment', data),
  
  recuperarCobrosPerdidos: () =>
    api.post('/audit-finance/recuperar-cobros-perdidos', {}),
};

// API de mensajes
export const messagesApi = {
  send: (data: { toUserId: number; contenido: string; tipo?: string; prioridad?: string; isReply?: boolean }) =>
    api.post('/messages', data),

  getUserMessages: (userId: number) =>
    api.get(`/messages/user/${userId}`),

  getMyMessages: () =>
    api.get('/messages/my-messages'),

  // Conversaciones estilo WhatsApp
  getConversations: () =>
    api.get('/messages/conversations'),

  getConversation: (userId: number) =>
    api.get(`/messages/conversation/${userId}`),

  deleteConversation: (userId: number) =>
    api.delete(`/messages/conversation/${userId}`),

  markAsRead: async (messageId: number) => {
    const response = await api.put(`/messages/${messageId}/read`);
    return response;
  },

  getUnreadCount: async () => {
    const response = await api.get('/messages/unread-count');
    return response;
  },

  delete: (messageId: number) =>
    api.delete(`/messages/${messageId}`),
};

// API de sesiones de trabajo
export const workSessionApi = {
  getEmployeeSummary: (mes?: number, anio?: number, search?: string, userId?: number) =>
    api.get('/work-sessions/employee-summary', { params: { mes, anio, search, userId } }),
  
  getUserHours: (userId: number, fecha?: string) =>
    api.get(`/work-sessions/user-hours/${userId}`, { params: { fecha } }),
  
  getReport: (mes?: number, anio?: number, userId?: number) =>
    api.get('/work-sessions/report', { params: { mes, anio, userId } }),

  // Autorización de horas extra (solo supervisores)
  authorizeOvertime: (userId: number) =>
    api.post(`/work-sessions/authorize-overtime/${userId}`),
  
  revokeOvertime: (userId: number) =>
    api.post(`/work-sessions/revoke-overtime/${userId}`),

  // Cerrar todas las sesiones activas (solo admin)
  closeAllSessions: () =>
    api.post('/work-sessions/close-all-sessions'),

  // Cerrar sesión de un usuario específico
  closeUserSession: (userId: number) =>
    api.post(`/work-sessions/close-user-session/${userId}`),

  // Obtener conteos diarios de auditorías completadas
  getDailyAuditCounts: (userId: number, startDate?: string, endDate?: string) =>
    api.get(`/work-sessions/daily-audit-counts/${userId}`, { params: { startDate, endDate } }),

  // Obtener detalle de un día específico
  getDayDetail: (userId: number, fecha: string) =>
    api.get(`/work-sessions/day-detail/${userId}/${fecha}`),
};

// API de gastos
export const gastosApi = {
  getAll: (params?: any) =>
    api.get('/gastos', { params }),
  
  getById: (id: number) =>
    api.get(`/gastos/${id}`),
  
  create: (gastoData: any) =>
    api.post('/gastos', gastoData),
  
  update: (id: number, gastoData: any) =>
    api.put(`/gastos/${id}`, gastoData),
  
  delete: (id: number) =>
    api.delete(`/gastos/${id}`),
};

// =============================================================================
// OPERATOR PERMISSIONS API - Sistema de Permisos Granulares
// =============================================================================

export const operatorPermissionsApi = {
  // === OPERADORES BLOQUEADOS ===

  // Obtener operadores bloqueados para un cliente específico
  getBlockedOperators: (clientId: number) =>
    api.get(`/operator-permissions/blocked-operators/${clientId}`),

  // Bloquear un operador para un cliente específico
  blockOperator: (clientId: number, operatorId: number, reason?: string) =>
    api.post('/operator-permissions/block-operator', {
      clientId,
      operatorId,
      reason
    }),

  // Desbloquear un operador para un cliente específico
  unblockOperator: (clientId: number, operatorId: number) =>
    api.delete(`/operator-permissions/unblock-operator/${clientId}/${operatorId}`),

  // === CLIENTES ASIGNADOS ===

  // Obtener clientes asignados a un operador específico
  getAssignedClients: (operatorId: number) =>
    api.get(`/operator-permissions/assigned-clients/${operatorId}`),

  // Obtener operadores asignados a un cliente específico
  getAssignedOperators: (clientId: number) =>
    api.get(`/operator-permissions/assigned-operators/${clientId}`),

  // Asignar un cliente a un operador específico
  assignClient: (operatorId: number, clientId: number) =>
    api.post('/operator-permissions/assign-client', {
      operatorId,
      clientId
    }),

  // Desasignar un cliente de un operador específico
  unassignClient: (operatorId: number, clientId: number) =>
    api.delete(`/operator-permissions/unassign-client/${operatorId}/${clientId}`),

  // === UTILIDADES ===

  // Obtener operadores disponibles para asignar (no bloqueados) para un cliente
  getAvailableOperators: (clientId: number) =>
    api.get(`/operator-permissions/available-operators/${clientId}`),

  // Obtener clientes disponibles para asignar a un operador
  getAvailableClients: (operatorId: number) =>
    api.get(`/operator-permissions/available-clients/${operatorId}`),
};

// ============================================================================
// SISTEMA DE TRÁNSITO DE AUDITORÍAS - COLA DE GESTIÓN
// ============================================================================

export const auditTransitApi = {
  // Obtener auditorías en Cola de Gestión (pendientes de validación)
  getColaGestion: (params?: { origen?: string; clientId?: number }) =>
    api.get('/audits/cola-gestion', { params }),

  // Crear auditoría programada (directamente en cola)
  crearAuditoriaProgramada: (data: {
    clienteId: number;
    branchId?: number;
    titulo: string;
    descripcion?: string;
    sucursal?: string;
    direccion?: string;
    notas?: string;
    fechaSolicitada: string;
    fechaProgramada?: string;
    prioridad?: 'normal' | 'prioritaria' | 'urgente';
    operadorId?: number;
  }) => api.post('/audits/crear-programada', data),

  // Editar auditoría en Cola de Gestión
  editarAuditoriaCola: (id: number, data: {
    clienteId?: number;
    branchId?: number;
    titulo?: string;
    descripcion?: string;
    fechaSolicitada?: string;
    fechaProgramada?: string;
    prioridad?: string;
    operadorId?: number;
    notas?: string;
  }) => api.put(`/audits/${id}/editar-cola`, data),

  // Habilitar auditoría para tránsito (liberar al flujo normal)
  habilitarTransito: (id: number, data?: {
    operadorId?: number;
    fechaProgramada?: string;
    prioridad?: string;
  }) => api.post(`/audits/${id}/habilitar-transito`, data),

  // Pausar tránsito (sacar del flujo y volver a cola)
  pausarTransito: (id: number, data: { motivo?: string }) =>
    api.post(`/audits/${id}/pausar-transito`, data)
};

export default api;